import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _toConsumableArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@i18n";
import { useLang } from "@/common/Contexts/LanguageContext";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import useHelpers from "@hooks/useHelpers";
import useSideMenuController, { dispatchSideMenuOpenClose } from "@/_LayoutsRelated/layoutHooks/useSideMenuController";
import SyarahLogo from "@/components/SyarahLogo/SyarahLogo";
import BuildInfo from "../BuildInfo";
import useNavItemTracker, { navItemName } from "../navEvents";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/common/lib/utils";
import desktopNavData from "../DesktopHeader/desktopNavData";
import * as DrawerIcon from "./drawerNavIcons";

/**
 * Drawer artwork, replacing the mega-menu glyph every row would otherwise
 * inherit from `desktopNavData`.
 *
 * The two menus do not share a set: desktop is filled, the drawer outlined, and
 * several rows are drawn as different objects entirely — `مبيعات الشركات` is a
 * briefcase on desktop and a car with transfer arrows here. See the file header
 * in `drawerNavIcons` for why these are exports rather than Iconify ids.
 *
 * Figma 4121:23761 (signed in) / 4121:23762 (signed out).
 */
var DRAWER_ICON_OVERRIDES = {
  // حسابي — 4098:13200
  "nav-my-info": DrawerIcon.CarLineIcon,
  "nav-favorites": DrawerIcon.CarTransferIcon,
  "nav-track-orders": DrawerIcon.LocationPathIcon,
  "nav-saved-searches": DrawerIcon.SavedSearchIcon,
  "nav-recently-viewed": DrawerIcon.RecentlyViewedCarIcon,
  // تصفح في سيارة — 4096:12857
  "nav-all-cars": DrawerIcon.CarLineIcon,
  "nav-b2b-sales": DrawerIcon.CarTransferIcon,
  "nav-order-tracking": DrawerIcon.CarTransferIcon,
  "nav-cars-offers": DrawerIcon.CarOfferBadgeIcon,
  "nav-used-cars-offers": DrawerIcon.RosettePercentIcon,
  "nav-new-cars-offers": DrawerIcon.TicketPercentIcon,
  "nav-finance-calculator": DrawerIcon.CalculatorIcon,
  "nav-financing": DrawerIcon.CarSparkleIcon,
  "nav-trend": DrawerIcon.TrendSquareIcon,
  "nav-showrooms": DrawerIcon.GarageIcon,
  "nav-compare": DrawerIcon.CarTransferIcon,
  // تعرف على سيارة — 4090:11958
  "nav-contact-us": DrawerIcon.PhoneIcon,
  "nav-about-us": DrawerIcon.BuildingIcon,
  "nav-cities": DrawerIcon.CityBuildingsIcon,
  "nav-services": DrawerIcon.CarGearsIcon,
  "nav-carsguide": DrawerIcon.DocumentIcon,
  "nav-car-prices": DrawerIcon.CarRiyalIcon,
  "nav-affiliate": DrawerIcon.AffiliateIcon,
  "nav-service-centers": DrawerIcon.GearsIcon,
  "nav-careers": DrawerIcon.BriefcaseIcon,
  "nav-company-location": DrawerIcon.MapPinIcon,
  // تسجيل خروج — 4090:12220
  "nav-logout": DrawerIcon.LogoutIcon
};

/**
 * Row order for the drawer — Figma 4121:23761.
 *
 * Not derivable from `desktopNavData`: the desktop mega panels are two columns
 * read independently, so flattening them gives a different sequence than the
 * single list the drawer shows.
 */
var DRAWER_BROWSE_ORDER = ["nav-all-cars", "nav-b2b-sales", "nav-order-tracking", "nav-cars-offers", "nav-used-cars-offers", "nav-new-cars-offers", "nav-finance-calculator", "nav-financing", "nav-trend", "nav-showrooms", "nav-compare"];
var DRAWER_DISCOVER_ORDER = ["nav-contact-us", "nav-about-us", "nav-cities", "nav-services", "nav-carsguide", "nav-car-prices", "nav-affiliate", "nav-service-centers", "nav-careers", "nav-company-location"];

/** Rows the drawer omits entirely — Figma 4121:23761 lists neither. */
var DRAWER_EXCLUDED_IDS = ["nav-buy-cars", "nav-buy-services"];

/**
 * Applies the drawer order. Rows the order does not name keep their source
 * position at the end rather than being dropped, so a link added to the desktop
 * nav still surfaces on mobile instead of silently disappearing.
 */
var orderForDrawer = function orderForDrawer(order, links) {
  var byId = new Map(links.map(function (link) {
    return [link.id, link];
  }));
  var named = order.map(function (id) {
    return byId.get(id);
  }).filter(function (link) {
    return Boolean(link);
  });
  var namedIds = new Set(order);
  var rest = links.filter(function (link) {
    return !link.id || !namedIds.has(link.id);
  });
  return [].concat(_toConsumableArray(named), _toConsumableArray(rest));
};
var withDrawerIcons = function withDrawerIcons(links) {
  return links.map(function (link) {
    return link.id && DRAWER_ICON_OVERRIDES[link.id] ? _objectSpread(_objectSpread({}, link), {}, {
      IconComponent: DRAWER_ICON_OVERRIDES[link.id]
    }) : link;
  });
};
/** Figma 4121:23761 — icon at the inline start, label beside it, chevron pinned to the end. */
var MenuRow = function MenuRow(_ref) {
  var link = _ref.link,
    group = _ref.group;
  var track = useNavItemTracker("hamburger_menu");
  var isAction = Boolean(link.onSelect) && link.href === "#";
  var Row = isAction ? "button" : "a";
  var onClick = function onClick() {
    var _link$onSelect;
    track(navItemName(link.id), group);
    (_link$onSelect = link.onSelect) === null || _link$onSelect === void 0 ? void 0 : _link$onSelect.call(link);
  };
  var rowProps = isAction ? {
    type: "button",
    onClick: onClick
  } : {
    href: link.href,
    target: link.isExternal ? "_blank" : undefined,
    rel: link.isExternal ? "noopener noreferrer" : undefined,
    onClick: onClick
  };
  return /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement(Row, _extends({}, rowProps, {
    id: link.id
    // Same pre-hydration click-replay contract as the desktop nav: the
    // catcher in script_loader_renderer.ts only scans 2 levels up for an
    // `id`, and the tap target here is a nested <span>.
    ,
    "data-id": link.id,
    className: "flex h-[38px] w-full cursor-pointer flex-row items-center gap-2 border-0 bg-transparent p-0 text-start text-navy-primary no-underline"
  }), /*#__PURE__*/React.createElement("span", {
    className: "flex size-[30px] shrink-0 items-center justify-center"
  }, link.IconComponent ? /*#__PURE__*/React.createElement(link.IconComponent, {
    "aria-hidden": "true",
    width: 30,
    height: 30
  }) : link.icon && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true",
    className: cn("iconify", link.icon),
    style: {
      width: 20,
      height: 20
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: "flex-1 truncate text-sm leading-[22px]"
  }, link.title), /*#__PURE__*/React.createElement(DrawerIcon.CaretLeftIcon, {
    "aria-hidden": "true",
    width: 20,
    height: 20,
    className: "shrink-0 en:rotate-180"
  })));
};
var MenuList = function MenuList(_ref2) {
  var links = _ref2.links,
    group = _ref2.group;
  return /*#__PURE__*/React.createElement("ul", {
    className: "m-0 flex list-none flex-col gap-2 p-0"
  }, links.map(function (link) {
    var _link$id;
    return /*#__PURE__*/React.createElement(MenuRow, {
      key: (_link$id = link.id) !== null && _link$id !== void 0 ? _link$id : link.href,
      link: link,
      group: group
    });
  }));
};
var MenuSection = function MenuSection(_ref3) {
  var title = _ref3.title,
    links = _ref3.links,
    group = _ref3.group;
  return links.length === 0 ? null : /*#__PURE__*/React.createElement("section", {
    role: "group",
    "aria-label": title,
    className: "rounded-2xl border border-solid border-[#e6e9ef] bg-[#fcfcfc] p-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "pb-4 text-base font-bold text-navy-primary"
  }, title), /*#__PURE__*/React.createElement(MenuList, {
    links: links,
    group: group
  }));
};
var flatten = function flatten(group) {
  return group.columns.flat();
};
var AvatarBubble = function AvatarBubble() {
  return /*#__PURE__*/React.createElement("span", {
    className: "inline-flex size-9 shrink-0 items-center justify-center rounded-full border-[1.385px] border-solid border-[#ffffff] bg-blue-lavender shadow-[0_0.692px_1.385px_0_rgba(74,58,255,0.07)]"
  }, /*#__PURE__*/React.createElement(DrawerIcon.UserBoldIcon, {
    "aria-hidden": "true",
    width: 20.77,
    height: 20.77,
    className: "text-primary-new"
  }));
};

/**
 * Responsive hamburger drawer. Replaces `NewAside`.
 *
 * Content comes from `desktopNavData`, the same source as the desktop mega
 * menus, so the two can never drift and every link stays reachable on mobile
 * (mobile-first indexing).
 *
 * Rendered through the Sheet's `inline` variant: no Portal, force-mounted, so
 * all links ship in the SSR HTML exactly as the old inline aside did.
 */
var MobileSideMenu = function MobileSideMenu(_ref4) {
  var switchLink = _ref4.switchLink;
  var _useTranslation = useTranslation(["web_aside_menu", "web_header", "web_common"]),
    t = _useTranslation.t,
    tShort = _useTranslation.tShort;
  var lang = useLang();
  var location = useLocation();
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl,
    createUrl2 = _useHelpers.createUrl2;
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    logout = _useAuthActions.logout;
  var isOpen = useSideMenuController();
  var trackNav = useNavItemTracker("hamburger_menu");
  var nav = desktopNavData({
    t: t,
    lang: lang,
    createUrl: createUrl,
    isLoggedIn: isLoggedIn,
    logout: logout
  });

  /*
   * `تعقب طلباتي` must be reachable in both states but listed only once.
   * Signed out there is no account card, so it rides in the browse list;
   * signed in the account card already carries it, so drop it from browse
   * rather than showing the same destination twice.
   */
  var hiddenDrawerIds = new Set([].concat(DRAWER_EXCLUDED_IDS, _toConsumableArray(isLoggedIn ? ["nav-order-tracking"] : [])));
  var drawerSection = function drawerSection(order, links) {
    return withDrawerIcons(orderForDrawer(order, links.filter(function (link) {
      return !link.id || !hiddenDrawerIds.has(link.id);
    })));
  };
  var accountLinks = withDrawerIcons(flatten(nav.account));
  // Logout is pinned to the drawer footer, so keep it out of the list.
  var accountRows = accountLinks.filter(function (l) {
    return l.id !== "nav-logout";
  });
  var logoutRow = accountLinks.find(function (l) {
    return l.id === "nav-logout";
  });

  /*
   * Scroll lock lives here rather than coming from radix. The drawer is
   * force-mounted for SSR, and radix's modal path would lock scroll and mark
   * the page `aria-hidden` permanently (see the `inline` note in sheet.tsx), so
   * the Sheet runs non-modal and we lock only while open — the same
   * `body.style.overflow` approach `UnbxdMobileTextSearch` uses.
   */
  useEffect(function () {
    if (!isOpen) return;
    var previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function () {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);
  return (
    /*#__PURE__*/
    // `m-show` = hidden at >=1025px; desktop uses the mega menus instead.
    React.createElement("div", {
      className: "m-show"
    }, /*#__PURE__*/React.createElement(Sheet, {
      open: isOpen,
      onOpenChange: dispatchSideMenuOpenClose,
      modal: false
    }, isOpen && /*#__PURE__*/React.createElement("div", {
      "aria-hidden": "true",
      onClick: function onClick() {
        return dispatchSideMenuOpenClose(false);
      }
      /* Arbitrary colour on purpose: tailwind.config redefines `black`
         as `{ primary }` with no DEFAULT, so `bg-black/40` compiles to
         nothing and the backdrop would be invisible. */,
      className: "fixed inset-0 z-[10000] bg-[rgba(0,0,0,0.4)]"
    }), /*#__PURE__*/React.createElement(SheetContent, {
      inline: true
      // The backdrop tap and the hamburger both close this, so the X the
      // Sheet ships by default was redundant and sat over the logo.
      ,
      hideClose: true
      /* Opens from the side opposite the reading direction: left in
         Arabic, right in English. The hamburger sits at the inline end of
         the header, so the drawer enters from the far edge rather than
         from under the control that opened it. */,
      side: lang === "ar" ? "left" : "right",
      "aria-describedby": undefined,
      onPointerDownOutside: function onPointerDownOutside(event) {
        var target = event.target;
        /* The layer is always mounted, so it also sees the tap on the
           hamburger that is opening the drawer. Ignore outside taps while
           closed, and taps on either menu trigger, or the drawer would
           close itself the instant it opens. */
        if (!isOpen || target !== null && target !== void 0 && target.closest('[data-id="mobile-nav-menu"], .sideMenuToggle')) {
          event.preventDefault();
        }
      },
      onFocusOutside: function onFocusOutside(event) {
        if (!isOpen) event.preventDefault();
      },
      className: "z-[10001] w-[308px] max-w-[85vw] items-center gap-5 overflow-y-auto rounded-s-2xl bg-[#fbfbfb] px-3 pb-10 pt-[30px] shadow-[0_0_45.5px_0_rgba(0,0,0,0.2)] sm:max-w-[308px]"
    }, /*#__PURE__*/React.createElement(SheetTitle, {
      asChild: true
    }, /*#__PURE__*/React.createElement("span", {
      className: "sr-only"
    }, t("menu", {
      ns: "web_header"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "flex w-full shrink-0 flex-col items-center gap-6"
    }, /*#__PURE__*/React.createElement(SyarahLogo, {
      width: 58,
      height: 27
    }), /*#__PURE__*/React.createElement("div", {
      className: "flex w-full flex-col gap-8"
    }, isLoggedIn ?
    /*#__PURE__*/
    /* User details and the account links share one glass card,
       separated by a hairline — Figma 4121:23761. */
    React.createElement("section", {
      className: "rounded-2xl border border-solid border-[#e6e9ef] bg-[#fcfcfc] p-4"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex min-h-[39px] items-center gap-2"
    }, /*#__PURE__*/React.createElement(AvatarBubble, null), /*#__PURE__*/React.createElement("span", {
      className: "flex min-w-0 flex-1 flex-col gap-1 text-start"
    }, /*#__PURE__*/React.createElement("strong", {
      className: "truncate text-sm font-semibold leading-[24px] text-navy-primary"
    }, t("welcome", {
      ns: "web_aside_menu"
    }), " ", (user === null || user === void 0 ? void 0 : user.full_name) || t("you", {
      ns: "web_aside_menu"
    })), (user === null || user === void 0 ? void 0 : user.phone_number) && /*#__PURE__*/React.createElement("span", {
      className: "truncate text-xs leading-[1.35] text-[#b0b0b0]"
    }, user.phone_number))), /*#__PURE__*/React.createElement("div", {
      className: "my-3 h-px w-full bg-[#e0e0e0]"
    }), /*#__PURE__*/React.createElement(MenuList, {
      links: accountRows,
      group: "my_account"
    })) : /*#__PURE__*/React.createElement("a", {
      id: "mobile-menu-login",
      "data-id": "mobile-menu-login",
      href: nav.loginHref,
      onClick: function onClick() {
        return trackNav("login", "my_account");
      },
      className: "flex h-[71px] items-center justify-between gap-2 rounded-xl border border-solid border-[#e6e9ef] bg-blue-alice p-4 text-navy-primary no-underline"
    }, /*#__PURE__*/React.createElement("span", {
      className: "flex min-w-0 items-center gap-2"
    }, /*#__PURE__*/React.createElement(AvatarBubble, null), /*#__PURE__*/React.createElement("span", {
      className: "truncate text-sm font-semibold leading-[24px]"
    }, t("login_sign_up", {
      ns: "web_aside_menu"
    }))), /*#__PURE__*/React.createElement(DrawerIcon.ArrowLeftIcon, {
      "aria-hidden": "true",
      width: 24,
      height: 24,
      className: "shrink-0 en:rotate-180"
    })), /*#__PURE__*/React.createElement("div", {
      className: "flex w-full flex-col gap-4"
    }, /*#__PURE__*/React.createElement(MenuSection, {
      title: nav.browse.title,
      links: drawerSection(DRAWER_BROWSE_ORDER, [].concat(_toConsumableArray(nav.mainLinks), _toConsumableArray(flatten(nav.browse)))),
      group: "browse_syarah"
    }), /*#__PURE__*/React.createElement(MenuSection, {
      title: nav.discover.title,
      links: drawerSection(DRAWER_DISCOVER_ORDER, flatten(nav.discover)),
      group: "discover_syarah"
    })))), /*#__PURE__*/React.createElement("div", {
      className: "flex w-full flex-col items-center gap-8"
    }, /*#__PURE__*/React.createElement("a", {
      id: "mobile-menu-lang",
      "data-id": "mobile-menu-lang"
      /* `keepQuery` — see the desktop switcher: dropping the query
         strands pages keyed on it, such as the araboon checkout. */,
      href: switchLink !== null && switchLink !== void 0 ? switchLink : createUrl2(location.pathname + location.search, {}, true),
      onClick: function onClick() {
        return trackNav("language_switch");
      },
      className: "flex h-12 w-[208.5px] max-w-full items-center justify-center gap-1 rounded-[31px] border border-solid border-[#e6e9ef] bg-[rgba(255,255,255,0.1)] text-sm tracking-[-0.28px] text-navy-primary no-underline"
    }, /*#__PURE__*/React.createElement("span", null, tShort("switch_language", {
      ns: "web_common"
    })), /*#__PURE__*/React.createElement(DrawerIcon.GlobeIcon, {
      "aria-hidden": "true",
      width: 17,
      height: 17,
      className: "shrink-0"
    })), logoutRow && /*#__PURE__*/React.createElement("button", {
      type: "button",
      id: logoutRow.id,
      "data-id": logoutRow.id,
      onClick: function onClick() {
        var _logoutRow$onSelect;
        trackNav(navItemName(logoutRow.id), "my_account");
        (_logoutRow$onSelect = logoutRow.onSelect) === null || _logoutRow$onSelect === void 0 ? void 0 : _logoutRow$onSelect.call(logoutRow);
      },
      className: "flex cursor-pointer items-center justify-center gap-1 border-0 bg-transparent p-0 text-base text-[#f22c2c]"
    }, /*#__PURE__*/React.createElement("span", null, logoutRow.title), logoutRow.IconComponent ? /*#__PURE__*/React.createElement(logoutRow.IconComponent, {
      "aria-hidden": "true",
      width: 30,
      height: 30
    }) : logoutRow.icon && /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      className: cn("iconify", logoutRow.icon),
      style: {
        width: 20,
        height: 20
      }
    })), /*#__PURE__*/React.createElement(BuildInfo, null)))))
  );
};
export default MobileSideMenu;